import React, {useState, useEffect} from 'react';
import moment from 'moment-timezone';

import Modal from '../components/ModalNew';
import Button, {ButtonSizes} from '../components/ButtonNew';
import Link from '../components/Link';
import {Spinner} from '../components/Spinner';
import DuetSettings from './DuetSettings';
import DashboardSocketClient from './DashboardSocketClient';
import QuestionSuggestions, {SuggestedQuestion} from './QuestionSuggestions';
import {useAppContext, useAnalytics, useWindowSize} from '../hooks';
import {
  formatTime,
  getNextMatchTime,
  createURLWithParams,
  formatMediaStreamTrack,
  formatNetworkConnection,
  hideAIFeedbackFeatures,
} from '../util';
import matching from '../../assets/images/duet/matching.svg';
import noMatch from '../../assets/images/duet/no_match.svg';
import {InterviewTypes, SocketConnectionTypes} from '../../../lib/constants/duet';

const MOBILE_WIDTH = 768;

export const Stages = {
  SETUP: 'setup',
  WAITING: 'waiting',
  MATCHED: 'matched',
  NOT_MATCHED: 'not_matched',
  ERROR: 'error',
};

const matchSound = typeof Audio !== 'undefined' ? new Audio('/sounds/hero_decorative-celebration-01.wav') : undefined;
const errorSound = typeof Audio !== 'undefined' ? new Audio('/sounds/alert_error-01.wav') : undefined;

const UnmatchedModal = ({onReschedule}) => {
  const {tracker} = useAnalytics();

  return (
    <>
      <div className="mb-4 mx-auto h-12 w-12 rounded-full">
        <img src={noMatch.src} alt="error_icon" />
      </div>
      <div className="text-base text-center px-4">
        <p className="mb-4">
          {'We\'re sorry, it looks like we couldn\'t find you a match this time. We suggest scheduling another upcoming session.'}
        </p>
        <Button
          onClick={onReschedule}
          size={ButtonSizes.LARGE}
          className="mb-10"
        >
          Schedule upcoming session
        </Button>
        <p className="mb-4">
          {'In the meantime, visit the '}
          <Link
            href="/questions"
            onClick={() => tracker.duetClickSocrates()}
            className="text-indigo-600 font-semibold"
            target="_blank"
          >
            Question Bank
          </Link>
          {' to practice answering questions and receiving feedback from peers.'}
        </p>
      </div>
    </>
  );
};

const DuetJoiningModal = ({
  isOpen,
  setPageTitle,
  onClose,
  onReschedule,
  nextMatchTimes,
  requestToJoin,
  initialStage = Stages.SETUP,
}) => {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();
  const [request, setRequest] = useState(requestToJoin);
  const [stage, setStage] = useState(initialStage);
  const [match, setMatch] = useState(null);
  const [client, setClient] = useState(null);
  const [countdown, setCountdown] = useState(Infinity);
  const [suggestedQuestion, setSuggestedQuestion] = useState(null);
  const {width} = useWindowSize();
  const isMobile = width < MOBILE_WIDTH;

  // Store most recent requestToJoin to avoid race conditions
  useEffect(() => {
    if (requestToJoin) setRequest(requestToJoin);
  }, [requestToJoin]);

  useEffect(() => {
    const nextMatchTimesForType = nextMatchTimes?.[request?.type];
    if (nextMatchTimesForType) {
      (function updateCountdown() {
        const matchTimeObj = getNextMatchTime(nextMatchTimesForType);
        const secondsUntilNextMatch = moment(matchTimeObj.matchTime).diff(moment(), 'seconds');
        setCountdown(secondsUntilNextMatch);
        setTimeout(updateCountdown, 1000);
      }());
    }
  }, [nextMatchTimes, request]);

  const handleSubmitSettings = async (settings) => {
    const platformData = {
      user_agent: navigator.userAgent,
      audio_track: formatMediaStreamTrack(settings.audioTracks[0]),
      video_track: formatMediaStreamTrack(settings.videoTracks[0]),
      network: formatNetworkConnection(navigator.connection), // undefined for non-Chromium browsers
    };

    client.emitSetupComplete({
      platformData,
      enableAIFeedback: settings.enableAIFeedback,
      allowRecordingConsent: settings.allowRecordingConsent,
    });
    tracker.duetJoinWaitingRoom({enableAIFeedback: settings.enableAIFeedback});
    setStage('waiting');
  };

  const updateSuggestedQuestion = (question) => {
    tracker.duetClickSuggestedQuestion({
      interviewType: request.type,
      id: question.id,
      slug: question.slug,
    });
    setSuggestedQuestion(question);
  };

  // Set up socket connection
  useEffect(() => {
    const onMatch = (data) => {
      setMatch(data);
      setStage('matched');
      setPageTitle('You\'re matched!');
      matchSound.play();
      tracker.duetGetMatch();
    };

    const onNoMatch = async () => {
      setStage('not_matched');
      errorSound.play();
      tracker.duetNoMatch();
    };

    const onError = async () => {
      setStage('error');
      errorSound.play();
      tracker.duetMatchError();
    };

    let socketClient = null;
    if (isOpen && request && !isMobile) {
      socketClient = new DashboardSocketClient({
        onReceiveMatch: onMatch,
        onReceiveNoMatch: onNoMatch,
        onReceiveError: onError,
        connect_info: {
          type: SocketConnectionTypes.MATCHING,
          user_id: currentUser.id,
          session_time: request.sessionTime,
        },
      });
      socketClient.connect();
      setClient(socketClient);
    }
    return () => {
      if (socketClient) socketClient.disconnect();
    };
  }, [isOpen, request, isMobile, setPageTitle, currentUser, tracker]);

  const hasPreassignedQuestion = InterviewTypes[request?.type]?.hasPreassignedQuestions;

  if (isMobile) {
    return (
      <Modal
        header="Looks like you're on mobile!"
        isOpen={isOpen}
        onClose={onClose}
        showCloseButton
      >
        <p className="text-base text-left mb-6">
          {'Peer mock interviews aren\'t currently supported on mobile devices. We recommend using a computer for the best experience!'}
        </p>
        <Button onClick={onClose} className="w-full">
          Back
        </Button>
      </Modal>
    );
  }

  switch (stage) {
    case (Stages.SETUP):
      return (
        <Modal
          header="Lights, camera, action!"
          isOpen={isOpen}
        >
          <p className="mb-6 text-sm text-gray-500 font-medium text-center">
            Set up your camera so your partner will be able to see you.
            Click “Allow” when prompted to give camera access.
          </p>
          <DuetSettings
            onSubmit={handleSubmitSettings}
            hideAIFeedbackToggle={hideAIFeedbackFeatures(currentUser, request)}
            showRecordingConsent
          />
        </Modal>
      );
    case (Stages.WAITING):
      return (
        <Modal
          header="Waiting for your partner..."
          isOpen={isOpen}
          modalClassName="overflow-visible"
        >
          <div className="w-full flex justify-center my-8">
            <Spinner className="w-16 h-16" />
          </div>
          <div className="text-base text-gray-800 font-medium text-center mb-6">
            {countdown < Infinity ? `Estimated time remaining: ${formatTime(countdown)}` : ''}
          </div>
          {!hasPreassignedQuestion && !suggestedQuestion && (
            <div className="-mx-4 -mb-4 px-6 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-800 font-medium mb-2">
                Don&apos;t have a question prepared? Select one from our question bank.
              </div>
              <div className="h-96 flex flex-col">
                <QuestionSuggestions
                  initialRole={InterviewTypes[request.type].tags?.role}
                  initialCategory={InterviewTypes[request.type].tags?.type}
                  onSelectQuestion={updateSuggestedQuestion}
                />
              </div>
            </div>
          )}
          {!hasPreassignedQuestion && suggestedQuestion && (
            <div className="-mx-4 px-6 pt-6 pb-3 border-t border-gray-200">
              <SuggestedQuestion
                question={suggestedQuestion}
                onShowMore={() => setSuggestedQuestion(null)}
                className="text-indigo"
              />
            </div>
          )}
        </Modal>
      );
    case (Stages.MATCHED):
      return (
        <Modal
          header="You got matched!"
          isOpen={isOpen}
        >
          <div className="w-full flex flex-col items-center mb-6">
            <img src={matching.src} alt="matched" className="w-16 h-16 mb-10" />
            <Button
              href={createURLWithParams(`/practice/${match.room}`, {q: suggestedQuestion?.id})}
              onClick={() => tracker.duetJoinMatch()}
              size={ButtonSizes.LARGE}
            >
              Join your interview
            </Button>
          </div>
        </Modal>
      );
    case (Stages.NOT_MATCHED):
      return (
        <Modal
          header="We couldn't find you a match"
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton
        >
          <UnmatchedModal onReschedule={onReschedule} />
        </Modal>
      );
    case (Stages.ERROR):
      return (
        <Modal
          header="We couldn't find you a match"
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton
        >
          <UnmatchedModal onReschedule={onReschedule} />
        </Modal>
      );
    default:
      throw new Error(`Invalid stage for DuetJoiningModal: ${stage}`);
  }
};

export default DuetJoiningModal;
